import { useState } from 'react';

import LanguageLocales from '@helsenorge/core-utils/constants/languages';
import { isExternalHttpUrl } from '@helsenorge/core-utils/url';
import { getUserLanguage, useSetLanguageEvent } from '@helsenorge/framework-utils/hn-language';
import { warn } from '@helsenorge/framework-utils/logger';
import type { Uthopp } from '@helsenorge/framework-utils/types/Resources';
import { UTHOPP_CANCEL_EVENT_NAME, UTHOPP_CONFIRM_EVENT_NAME } from '@helsenorge/framework-utils/uthopp/events';
import { getResources } from '@helsenorge/framework-utils/uthopp/resources';
import type { UthoppContainerProps } from '@helsenorge/framework-utils/uthopp/types';

import { useUthoppEvent } from './useUthoppEvent';
import UthoppDialog from './uthopp-dialog';

interface UthoppState {
  isOpen: boolean;
  url: string | null;
  resources?: Partial<Uthopp>;
}

const UthoppContainer: React.FC<UthoppContainerProps> = props => {
  const [language, setLanguage] = useState<LanguageLocales>(props.language || getUserLanguage() || LanguageLocales.NORWEGIAN);
  const resources = getResources(language);
  const [state, setState] = useState<UthoppState>({
    isOpen: false,
    url: null,
    resources: undefined,
  });

  const handleConfirm = (): void => {
    document.dispatchEvent(new CustomEvent(UTHOPP_CONFIRM_EVENT_NAME, { detail: { url: state.url } }));
  };

  const handleCancel = (): void => {
    document.dispatchEvent(new CustomEvent(UTHOPP_CANCEL_EVENT_NAME, { detail: { url: state.url } }));

    setState(prev => ({ ...prev, isOpen: false }));
  };

  useSetLanguageEvent(newLanguage => {
    setLanguage(newLanguage);
  });

  useUthoppEvent(({ url, resources }) => {
    // Verdien kan komme fra JavaScript eller eventdata uten typesjekk
    if (typeof url !== 'string' || !isExternalHttpUrl(url)) {
      warn(`Uthopp-URL er ikke en ekstern URL: ${url}`);
      return;
    }

    setState({ isOpen: true, url, resources });
  });

  return (
    <UthoppDialog
      isOpen={state.isOpen}
      url={state.url ?? ''}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
      resources={{
        ...resources,
        ...state.resources,
      }}
    />
  );
};

export default UthoppContainer;
